import './instruments.js';

import './App.css';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';

import { Container } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';

import ApolloProviderWrapper from './apollo/ApolloProviderWrapper';
import styles from './app.module.css';
import { EMAIL_CHECK_URL, OTP_CHECK_URL } from './app/constants/constants';
import DialogsWrapper from './app/wrapper/DialogsWrapper';
import NativeWrapper from './app/wrapper/NativeWrapper';
import AppInitializers from './components/AppInitializers';
import AmazonReviewPromptGate from './components/amazonReview/AmazonReviewPromptGate';
import OfflinePageWrapper from './components/authentication/OfflinePageWrapper';
import PostHogIdentify from './components/authentication/PostHogIdentify';
import PrivateRoute from './components/authentication/PrivateRoute';
import SignInContainer from './components/authentication/SignInContainer';
import { UpAuth } from './components/authentication/UpAuth';
import DeviceRegistration from './components/deviceRegistration/DeviceRegistration';
import FirebaseToken from './components/firebase/FirebaseToken';
import I18n from './components/i18n/I18n';
import Miniplayer from './components/miniplayer/Miniplayer';
import RateUpliance from './components/rateUpliance/RateUpliance';
import ViewRateUpliance from './components/rateUpliance/ViewRateUpliance';
import RecipeCreateForm from './components/recipeCreation/recipeCreateForm';
import UpConnect from './core/UpConnect/UpConnect';
import UpWSConnect from './core/UpWSConnect/UpWSConnect';
import BluetoothConnection from './pages/BluetoothConnection';
import CookingHistory from './pages/cookingHistory/CookingHistory';
import CookingHistoryEdit from './pages/cookingHistory/CookingHistoryEdit';
import EmailCheck from './pages/EmailCheck';
import GroceryListContainer from './pages/groceryList/GroceryList';
import Homepage from './pages/Homepage';
import AddMealToMealPlan from './pages/mealPlan/AddMeal';
import MealPlanWithAi from './pages/mealPlan/MealPlanWithAi';
import NotFound404 from './pages/notFoundPage/NotFound404';
import OtpPage from './pages/OtpPage';
import RecipeCreationSteps from './pages/recipeCreationSteps';
import RecipePage from './pages/recipePage';
import RecipeReviewPage from './pages/RecipeReviewPage/RecipeReviewPage';
import SignIn from './pages/SignIn';
import FilteredRecipe from './pages/specificCategoryPage';
import UserFavorites from './pages/UserFavorites';
import ccTheme from './theme/Experience2Theme';
import ErrorManagementProvider from './utils/errorManagement/ErrorManagementProvider';
import LocationProvider from './utils/location/LocationProvider';

declare global {
  interface Window {
    // firebase globals
    recaptchaVerifier: any;
    confirmationResult: any;
    showAppRating?: () => void;
    onAppRatingPosted?: (success: boolean) => void;
  }
}

function App(): JSX.Element {
  return (
    <div className="App">
      <I18n>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={ccTheme}>
            <Container className={styles.wrapper}>
              <SnackbarProvider className={styles.snackbar_provider} dense>
                <ErrorManagementProvider>
                  <ApolloProviderWrapper>
                    <UpAuth>
                      <AppInitializers />
                      <NativeWrapper>
                        <PostHogIdentify>
                          <FirebaseToken />
                          <DialogsWrapper>
                            <Router>
                              <AmazonReviewPromptGate />
                              <LocationProvider>
                                <OfflinePageWrapper>
                                  <UpConnect>
                                    <UpWSConnect>
                                      <Miniplayer />
                                      <Switch>
                                        <Route exact path="/sign-in">
                                          <SignInContainer>
                                            <SignIn />
                                          </SignInContainer>
                                        </Route>
                                        <Route exact path={OTP_CHECK_URL}>
                                          <SignInContainer>
                                            <OtpPage />
                                          </SignInContainer>
                                        </Route>
                                        <Route exact path={EMAIL_CHECK_URL}>
                                          <SignInContainer>
                                            <EmailCheck />
                                          </SignInContainer>
                                        </Route>
                                        <Route exact path="/">
                                          <Homepage defaultTabIndex={0} />
                                        </Route>

                                        <PrivateRoute exact path="/explore">
                                          <Homepage defaultTabIndex={1} />
                                        </PrivateRoute>
                                        <PrivateRoute exact path="/meal-plan">
                                          <Homepage defaultTabIndex={2} />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          exact
                                          path="/meal-plan/ai"
                                        >
                                          <MealPlanWithAi />
                                        </PrivateRoute>
                                        <PrivateRoute exact path="/account">
                                          <Homepage defaultTabIndex={3} />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          exact
                                          path="/meal-plan/add-meal"
                                        >
                                          <AddMealToMealPlan />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          exact
                                          path="/mobile-login/:deviceId"
                                        >
                                          <DeviceRegistration />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          exact
                                          path="/user-create-recipe"
                                        >
                                          <RecipeCreateForm />
                                        </PrivateRoute>
                                        <PrivateRoute path="/filter">
                                          <FilteredRecipe />
                                        </PrivateRoute>
                                        <PrivateRoute path="/favorites">
                                          <UserFavorites />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          exact
                                          path="/cooking-history"
                                        >
                                          <CookingHistory />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          exact
                                          path="/grocery-list"
                                        >
                                          <GroceryListContainer />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          exact
                                          path="/account/rate-upliance"
                                        >
                                          <RateUpliance />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          exact
                                          path="/account/view-rate-upliance"
                                        >
                                          <ViewRateUpliance />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          exact
                                          path="/cooking-history/edit/:sessionId"
                                        >
                                          <CookingHistoryEdit />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          exact
                                          path="/recipe/:recipeId"
                                        >
                                          <RecipePage />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          exact
                                          path="/recipe/:recipeId/reviews"
                                        >
                                          <RecipeReviewPage />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          exact
                                          path="/bluetooth-connection"
                                        >
                                          <BluetoothConnection />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          exact
                                          path="/recipe/:recipeId/steps"
                                        >
                                          <RecipeCreationSteps />
                                        </PrivateRoute>
                                        <Route>
                                          <NotFound404 />
                                        </Route>
                                      </Switch>
                                    </UpWSConnect>
                                  </UpConnect>
                                </OfflinePageWrapper>
                              </LocationProvider>
                            </Router>
                          </DialogsWrapper>
                        </PostHogIdentify>
                      </NativeWrapper>
                    </UpAuth>
                  </ApolloProviderWrapper>
                </ErrorManagementProvider>
              </SnackbarProvider>
            </Container>
          </ThemeProvider>
        </StyledEngineProvider>
      </I18n>
    </div>
  );
}

export default App;
